import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { useT } from './i18n/index';

interface PlanSettingsDialogProps {
  ganttStartDate: Date;
  onGanttStartDateChange: (d: Date, iso: string) => void;
  fullDaySchedule: boolean;
  onFullDayScheduleChange: (v: boolean) => void;
  workDayStartHour: number;
  workDayEndHour: number;
  onWorkDayHoursChange: (start: number, end: number) => void;
  workDays: number[];
  onWorkDaysChange: (days: number[]) => void;
  ganttNonWorkingDays: string[];
  onNonWorkingDaysChange: (days: string[]) => void;
  showStartDates: boolean;
  onShowStartDatesChange: (v: boolean) => void;
  showFinishDates: boolean;
  onShowFinishDatesChange: (v: boolean) => void;
  onClose: () => void;
}

const DAY_LABELS: { dow: number; label: string }[] = [
  { dow: 1, label: 'Mon' },
  { dow: 2, label: 'Tue' },
  { dow: 3, label: 'Wed' },
  { dow: 4, label: 'Thu' },
  { dow: 5, label: 'Fri' },
  { dow: 6, label: 'Sat' },
  { dow: 0, label: 'Sun' },
];

export default function PlanSettingsDialog({
  ganttStartDate,
  onGanttStartDateChange,
  fullDaySchedule,
  onFullDayScheduleChange,
  workDayStartHour,
  workDayEndHour,
  onWorkDayHoursChange,
  workDays,
  onWorkDaysChange,
  ganttNonWorkingDays,
  onNonWorkingDaysChange,
  showStartDates,
  onShowStartDatesChange,
  showFinishDates,
  onShowFinishDatesChange,
  onClose,
}: PlanSettingsDialogProps) {
  const { t } = useT();

  // Holidays sub-panel state
  const [showHolidays, setShowHolidays] = useState(false);
  const [holidayInput, setHolidayInput] = useState('');

  // Close on Escape
  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  const toggleWorkDay = (dow: number) => {
    const next = workDays.includes(dow)
      ? workDays.filter(d => d !== dow)
      : [...workDays, dow];
    onWorkDaysChange(next);
  };

  const addHoliday = () => {
    if (!holidayInput) return;
    const d = holidayInput.split('T')[0];
    if (!ganttNonWorkingDays.includes(d)) {
      onNonWorkingDaysChange([...ganttNonWorkingDays, d].sort());
    }
    setHolidayInput('');
  };

  const removeHoliday = (day: string) => {
    onNonWorkingDaysChange(ganttNonWorkingDays.filter(d => d !== day));
  };

  return (
    <>
      {/* Backdrop */}
      <div
        style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 500 }}
        onClick={onClose}
      />

      {/* Dialog */}
      <div style={{
        position: 'fixed', top: '50%', left: '50%',
        transform: 'translate(-50%,-50%)',
        background: 'white', borderRadius: 12,
        boxShadow: '0 12px 40px rgba(0,0,0,0.2)',
        zIndex: 501, width: 380,
        display: 'flex', flexDirection: 'column',
        maxHeight: '90vh', overflow: 'hidden',
      }}>
        {/* Header */}
        <div style={{ padding: '16px 20px 12px', borderBottom: '1px solid #e5e7eb', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: '#111827' }}>{t('planSettings')}</div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af', padding: 4 }}>
            <X size={16} />
          </button>
        </div>

        {/* Body */}
        <div style={{ padding: '16px 20px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 20 }}>

          {/* a) Default mode */}
          <div>
            <div style={{ fontSize: 12, fontWeight: 600, color: '#6b7280', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              {t('planDefaultMode')}
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              {[
                { label: t('workdayMode'), value: false },
                { label: '24h / day', value: true },
              ].map(opt => (
                <button
                  key={String(opt.value)}
                  onClick={() => onFullDayScheduleChange(opt.value)}
                  style={{
                    flex: 1, padding: '8px 12px', borderRadius: 8, border: '2px solid',
                    borderColor: fullDaySchedule === opt.value ? '#16697A' : '#e5e7eb',
                    background: fullDaySchedule === opt.value ? '#e6f4f5' : 'white',
                    color: fullDaySchedule === opt.value ? '#16697A' : '#374151',
                    fontWeight: fullDaySchedule === opt.value ? 700 : 400,
                    fontSize: 13, cursor: 'pointer',
                  }}
                >
                  {opt.label}
                </button>
              ))}
            </div>

            {!fullDaySchedule && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 10 }}>
                <span style={{ fontSize: 12, color: '#6b7280' }}>{t('workdayHours')}</span>
                <input
                  type="time"
                  step={3600}
                  value={`${String(workDayStartHour).padStart(2, '0')}:00`}
                  onChange={(e) => {
                    const h = parseInt(e.target.value.split(':')[0], 10);
                    if (!isNaN(h) && h < workDayEndHour) onWorkDayHoursChange(h, workDayEndHour);
                  }}
                  style={{ padding: '5px 8px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13 }}
                />
                <span style={{ fontSize: 12, color: '#9ca3af' }}>–</span>
                <input
                  type="time"
                  step={3600}
                  value={`${String(workDayEndHour).padStart(2, '0')}:00`}
                  onChange={(e) => {
                    const h = parseInt(e.target.value.split(':')[0], 10);
                    if (!isNaN(h) && h > workDayStartHour) onWorkDayHoursChange(workDayStartHour, h);
                  }}
                  style={{ padding: '5px 8px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13 }}
                />
              </div>
            )}
          </div>

          {/* b) Project start date */}
          <div>
            <div style={{ fontSize: 12, fontWeight: 600, color: '#6b7280', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              {t('planStartDate')}
            </div>
            <input
              type="date"
              value={ganttStartDate.toISOString().split('T')[0]}
              onChange={(e) => {
                if (!e.target.value) return;
                const d = new Date(e.target.value);
                if (!isNaN(d.getTime())) onGanttStartDateChange(d, e.target.value);
              }}
              style={{ width: '100%', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 13 }}
            />
          </div>

          {/* c) Holidays */}
          <div>
            <div style={{ fontSize: 12, fontWeight: 600, color: '#6b7280', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              {t('planHolidays')}
            </div>
            <button
              onClick={() => setShowHolidays(v => !v)}
              style={{
                width: '100%', padding: '8px 12px', borderRadius: 8,
                border: `1px solid ${ganttNonWorkingDays.length > 0 ? '#f59e0b' : '#d1d5db'}`,
                background: ganttNonWorkingDays.length > 0 ? '#fffbeb' : 'white',
                color: ganttNonWorkingDays.length > 0 ? '#b45309' : '#374151',
                fontWeight: 600, fontSize: 13, cursor: 'pointer', textAlign: 'left',
              }}
            >
              🗓 {t('holidays')}{ganttNonWorkingDays.length > 0 ? ` (${ganttNonWorkingDays.length})` : ''}
              <span style={{ float: 'right', fontWeight: 400 }}>{showHolidays ? '▲' : '▼'}</span>
            </button>

            {showHolidays && (
              <div style={{ marginTop: 8, border: '1px solid #e5e7eb', borderRadius: 8, padding: 12 }}>
                <div style={{ display: 'flex', gap: 6, marginBottom: 8 }}>
                  <input
                    type="date"
                    value={holidayInput}
                    onChange={e => setHolidayInput(e.target.value)}
                    style={{ flex: 1, padding: '6px 8px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13 }}
                  />
                  <button
                    onClick={addHoliday}
                    style={{ padding: '6px 12px', background: '#16697A', color: 'white', border: 'none', borderRadius: 6, fontSize: 13, cursor: 'pointer', fontWeight: 600 }}
                  >
                    {t('add')}
                  </button>
                </div>
                {ganttNonWorkingDays.length === 0 ? (
                  <div style={{ fontSize: 12, color: '#9ca3af', textAlign: 'center' }}>{t('noHolidays')}</div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4, maxHeight: 140, overflowY: 'auto' }}>
                    {ganttNonWorkingDays.map(day => (
                      <div key={day} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '4px 8px', background: '#f9fafb', borderRadius: 6, fontSize: 13 }}>
                        <span>{day}</span>
                        <button onClick={() => removeHoliday(day)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#ef4444', fontWeight: 700, fontSize: 14, lineHeight: 1, padding: '0 2px' }}>×</button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* d) Work days */}
          <div>
            <div style={{ fontSize: 12, fontWeight: 600, color: '#6b7280', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              {t('planWorkDays')}
            </div>
            <div style={{ display: 'flex', gap: 6 }}>
              {DAY_LABELS.map(({ dow, label }) => {
                const active = workDays.includes(dow);
                return (
                  <button
                    key={dow}
                    onClick={() => toggleWorkDay(dow)}
                    style={{
                      flex: 1, padding: '6px 0', borderRadius: 8, border: '2px solid',
                      borderColor: active ? '#16697A' : '#e5e7eb',
                      background: active ? '#16697A' : 'white',
                      color: active ? 'white' : '#9ca3af',
                      fontWeight: active ? 700 : 400,
                      fontSize: 11, cursor: 'pointer',
                    }}
                  >
                    {label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* e) Display options */}
          <div>
            <div style={{ fontSize: 12, fontWeight: 600, color: '#6b7280', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              Display
            </div>
            <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', padding: '8px 0' }}>
              <div
                onClick={() => onShowStartDatesChange(!showStartDates)}
                style={{
                  width: 36, height: 20, borderRadius: 10, border: '2px solid',
                  borderColor: showStartDates ? '#16697A' : '#d1d5db',
                  background: showStartDates ? '#16697A' : '#f3f4f6',
                  position: 'relative', cursor: 'pointer', transition: 'all 0.15s',
                  flexShrink: 0,
                }}
              >
                <div style={{
                  position: 'absolute', top: 1, left: showStartDates ? 16 : 1,
                  width: 14, height: 14, borderRadius: '50%', background: 'white',
                  transition: 'left 0.15s',
                }} />
              </div>
              <span style={{ fontSize: 13, color: '#374151' }}>Show start dates</span>
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', padding: '8px 0' }}>
              <div
                onClick={() => onShowFinishDatesChange(!showFinishDates)}
                style={{
                  width: 36, height: 20, borderRadius: 10, border: '2px solid',
                  borderColor: showFinishDates ? '#16697A' : '#d1d5db',
                  background: showFinishDates ? '#16697A' : '#f3f4f6',
                  position: 'relative', cursor: 'pointer', transition: 'all 0.15s',
                  flexShrink: 0,
                }}
              >
                <div style={{
                  position: 'absolute', top: 1, left: showFinishDates ? 16 : 1,
                  width: 14, height: 14, borderRadius: '50%', background: 'white',
                  transition: 'left 0.15s',
                }} />
              </div>
              <span style={{ fontSize: 13, color: '#374151' }}>Show finish dates</span>
            </label>
          </div>

        </div>

        {/* Footer */}
        <div style={{ padding: '12px 20px', borderTop: '1px solid #e5e7eb', display: 'flex', justifyContent: 'flex-end' }}>
          <button
            onClick={onClose}
            style={{ padding: '8px 20px', background: '#16697A', color: 'white', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: 'pointer' }}
          >
            {t('done')}
          </button>
        </div>
      </div>
    </>
  );
}
